import { $getSelection, $isRangeSelection } from 'lexical';
import { getTranslationForIso } from '../shared';
import { $createVariableNode } from '../../components/LexicalEditor/nodes/VariableNode';

/**
 * @typedef {import('../../components/LexicalEditor/plugins/ToolbarPlugin/Toolbar').IToolbarActionDefinition} IToolbarActionDefinition
 */

export const isEditedColorPicker = (isEdited) => {
    switch(isEdited) {
        case 'unedited': return 'danger';
        case 'edited': return 'success';
        default: return null;
    }
};

export const generateStatusesConfig = (statusEnum, translations) => {
    return Object.keys(statusEnum).map(key => ({
        id: parseInt(key),
        label: translations[statusEnum[key].toLowerCase()],
        color: isEditedColorPicker(statusEnum[key])
    }));
};

/**
 * @typedef NestedButtonsGeneratorParam
 * @property {LocalizedValue[]} category
 * @property {number} emailTemplateId
 * @property {number} id
 * @property {LocalizedValue[]} name
 * @property {string} value
 */

/**
 * @typedef {IToolbarActionDefinition} NestedButtonsGeneratorResult
 * @property {NestedButtonsGeneratorParam} param
 * @property {number} emailTemplateId
 * @property {number} id
 * @property {LocalizedValue[]} name
 * @property {string} value
 */

/**
 * @param {NestedButtonsGeneratorParam[]} params
 * @param {string} iso
 * @param {string} buttonName
 * @param {string[]} variables
 * @return {IToolbarActionDefinition[]}
 */
export const nestedButtonsGenerator = (params, iso, buttonName, variables) =>
{
    // eslint-disable-next-line no-undefined
    const pattern = variables ? new RegExp(`{(${variables.map(variable => variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})}`) : undefined;

    /**
     * @param {NestedButtonsGeneratorParam[]} params
     * @param {string} iso
     */
    const generateButtonCategories = (params, iso) =>
    {
        const extractedCategories = params.map(param => getTranslationForIso(param.category, iso));
        return [...new Set(extractedCategories)];
    };

    /** @type {CallbackFunction<IToolbarActionDefinition[]>} */
    const loadChildren = callback =>
    {
        const items = generateButtonCategories(params, iso).map(category => ({
            type: '',
            text: category,
            /** @type {CallbackFunction<IToolbarActionDefinition[]>} */
            loadChildren: (callback) =>
            {
                const subMenuItems = params.filter(param => getTranslationForIso(param.category, iso) === category).map(param =>
                {
                    return {
                        param,
                        type: 'menuitem',
                        text: getTranslationForIso(param.name, iso),
                        /** @type {(editor: import('lexical').LexicalEditor) => void} */
                        onSelected: (editor) =>
                        {
                            editor.update(() =>
                            {
                                const selection = $getSelection();
                                if (!$isRangeSelection(selection)) { return; }
                                const textNode = $createVariableNode(param.value, pattern);
                                selection.insertNodes([textNode]);
                            });
                        }
                    };
                });
                callback(subMenuItems.filter(item => item));
            }
        }));
        callback(items);
    };
    const item =
    {
        type: '',
        text: buttonName,
        loadChildren
    };
    return [item];
};
